import type {
  Board,
  BoardStroke,
  BoardView,
  Grant,
  ChatMessage,
  LiveRoom,
  LobbyEntry,
  MediaState,
  Participant,
  ParticipantId,
  ProducerInfo,
  Role,
  Room,
  TrackSource,
} from "@/lib/api/types";

export const PROTOCOL_VERSION = "bsmeet/1";
export const WS_PATH = "/ws";

export const AUTH_TIMEOUT_MS = 10_000;
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const HEARTBEAT_MAX_MISSED = 2;

export const CLOSE_UNAUTHORIZED = 4001;
export const CLOSE_FORBIDDEN = 4003;
export const CLOSE_AUTH_TIMEOUT = 4008;
export const CLOSE_PROTOCOL_ERROR = 4009;
export const CLOSE_SESSION_REVOKED = 4010;
export const CLOSE_REMOVED = 4011;
export const CLOSE_ROOM_ENDED = 4012;

export const CLOSE_CLIENT_RETRY = 4100;

export function isFatalClose(code: number): boolean {
  return (
    code === CLOSE_UNAUTHORIZED ||
    code === CLOSE_FORBIDDEN ||
    code === CLOSE_AUTH_TIMEOUT ||
    code === CLOSE_SESSION_REVOKED ||
    code === CLOSE_REMOVED ||
    code === CLOSE_ROOM_ENDED
  );
}

export type ClientEvents = {
  "session:auth": {
    protocol: string;
    accessToken?: string;
    guestName?: string;
    joinToken?: string;

    clientId?: string;
  };
  "session:ping": { ts: number };

  "room:join": {
    slug: string;
    passcode?: string;
    mic: boolean;
    cam: boolean;
    keepMedia?: boolean;
  };
  "room:leave": Record<string, never>;

  "media:state": Partial<MediaState>;
  "media:hand": { raised: boolean };

  "sfu:capabilities": Record<string, never>;
  "sfu:transport-create": { direction: "send" | "recv" };
  "sfu:transport-connect": { transportId: string; dtlsParameters: unknown };
  "sfu:produce": {
    transportId: string;
    kind: "audio" | "video";
    source: TrackSource;
    rtpParameters: unknown;
  };
  "sfu:producer-close": { producerId: string };
  "sfu:producer-pause": { producerId: string; paused: boolean };
  "sfu:consume-many": {
    transportId: string;
    rtpCapabilities: unknown;
    producers: { producerId: string; spatialLayer: number; temporalLayer: number }[];
  };
  "sfu:consumers-resume": { consumerIds: string[] };
  "sfu:consumer-layers": {
    layers: { consumerId: string; spatialLayer: number; temporalLayer: number }[];
  };
  "sfu:restart-ice": { transportId: string };

  "chat:send": { body: string; to?: ParticipantId };

  "board:open": { boardId: string };
  "board:close": Record<string, never>;
  "board:page": { page: number };
  "board:stroke": BoardStroke;
  "board:undo": { strokeId: string };
  "board:clear": { page: number };

  "board:view": BoardView;

  "board:pointer": { x: number; y: number; visible: boolean };

  "mod:role": { participantId: ParticipantId; role: Role };

  "mod:grant": { participantId: ParticipantId; grant: Grant; allowed: boolean };

  "mod:control": { participantId: ParticipantId };
  "mod:mute": { participantId: ParticipantId };
  "mod:mute-all": Record<string, never>;
  "mod:remove": { participantId: ParticipantId };
  "mod:lobby-admit": { participantId: ParticipantId };
  "mod:lobby-deny": { participantId: ParticipantId };
  "mod:settings": Partial<Room["settings"]>;
  "mod:end": Record<string, never>;
  "mod:recording": { active: boolean };
};

export type ClientEvent = keyof ClientEvents;

export type ServerEvents = {
  "session:ready": {
    protocol: string;

    displayName: string;
    userId: number | null;
    heartbeatIntervalMs: number;

    serverTime: string;

    bootId?: string;

    joinToken?: string;
  };
  "session:pong": { ts: number };
  "session:error": { code: string; message: string };
  "session:closed": { code: number; reason: string; fatal: boolean };

  "room:state": {
    room: LiveRoom;
    self: Participant;
    participants: Participant[];
    lobby: LobbyEntry[];
    producers: ProducerInfo[];
    board: Board | null;

    strokes: BoardStroke[];
    chat: ChatMessage[];
    recording: boolean;

    recordingSince: string | null;
  };
  "room:settings": Room["settings"];
  "room:ended": { reason: string };

  "participant:joined": Participant;
  "participant:left": { participantId: ParticipantId };
  "participant:updated": Participant;

  "participant:speaking": { participantIds: ParticipantId[] };

  "lobby:waiting": { entries: LobbyEntry[] };
  "lobby:admitted": Record<string, never>;
  "lobby:denied": { reason: string };

  "producer:added": ProducerInfo;
  "producer:removed": { producerId: string };
  "producer:paused": { producerId: string; paused: boolean };

  "chat:message": ChatMessage;

  "board:opened": Board;
  "board:closed": Record<string, never>;
  "board:page": { page: number };
  "board:stroke": BoardStroke;
  "board:undo": { strokeId: string };
  "board:cleared": { page: number };

  "board:strokes": BoardStroke[];
  "board:view": BoardView;
  "board:control": { participantId: ParticipantId; displayName: string };
  "board:pointer": {
    participantId: ParticipantId;
    displayName: string;
    x: number;
    y: number;
    visible: boolean;
  };
  "board:progress": { boardId: string; page: number; totalPages: number };
  "board:ready": Board;
  "board:failed": { boardId: string; error: string; superseded?: boolean };

  "recording:state": { active: boolean; startedAt: string | null };

  "mod:muted": { by: ParticipantId };
  "mod:mute-all-result": { count: number };
  "mod:removed": { reason: string };
};

export type ServerEvent = keyof ServerEvents;

export type OutboundFrame = {
  t: string;
  d?: unknown;

  i?: number;
};

export type InboundFrame = {
  t: string;
  d?: unknown;

  i?: number;

  e?: true;
};

export function encodeFrame(frame: OutboundFrame): string {
  return JSON.stringify(frame);
}

export function decodeFrame(raw: string): InboundFrame | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const frame = parsed as InboundFrame;
    return typeof frame.t === "string" && frame.t.length > 0 ? frame : null;
  } catch {
    return null;
  }
}
